import * as Matter from 'matter-js';
import type { SceneDef } from './types';
import { createGround, groundTopY } from './util';

export const pendulum: SceneDef = {
  labelKey: 'scene.pendulum',
  defaultPlaying: true,
  params: [
    { id: 'length', labelKey: 'dim.length', min: 80, max: 320, step: 10, value: 220 },
    { id: 'mass', labelKey: 'dim.mass', min: 0.5, max: 10, step: 0.5, value: 2 },
    { id: 'damping', labelKey: 'dim.damping', min: 0, max: 0.1, step: 0.005, value: 0.01 },
    { id: 'angle', labelKey: 'dim.angle', min: 5, max: 80, step: 1, value: 45 },
  ],
  build({ engine, world, dims, bounds }) {
    const pivotX = bounds.width / 2;
    const pivotY = Math.min(bounds.height * 0.12, 90);
    // Cap the effective length so the bob always clears the ground (and the action bar).
    const bobR = 18;
    const maxLength = groundTopY(bounds) - pivotY - bobR - 20;
    const length = Math.min(dims.length!, maxLength);
    const angleRad = (dims.angle! * Math.PI) / 180;
    const bobX = pivotX + Math.sin(angleRad) * length;
    const bobY = pivotY + Math.cos(angleRad) * length;

    const bob = Matter.Bodies.circle(bobX, bobY, bobR, {
      mass: dims.mass!,
      frictionAir: dims.damping!,
    });

    const rod = Matter.Constraint.create({
      pointA: { x: pivotX, y: pivotY },
      bodyB: bob,
      length,
      stiffness: 1,
      label: 'tension',
    });

    const ground = createGround(bounds);

    const bodies = [ground, bob];
    const constraints = [rod];
    Matter.World.add(world, [...bodies, ...constraints]);

    return {
      bodies,
      constraints,
      drawOverlay(ctx) {
        ctx.save();
        ctx.fillStyle = '#444';
        ctx.beginPath();
        ctx.arc(pivotX, pivotY, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      },
      appliedForces(body) {
        if (body !== bob) return [];
        // Rigid rod: Matter's constraint solver keeps `bob` exactly `length` from the pivot,
        // so the displacement-based force is ~0. The implied tension is computed analytically:
        //   T = m·g·cos(θ) + m·v_perp² / L
        // where θ is the rope angle from vertical and v_perp is the speed perpendicular to
        // the rope (purely tangential for a rigid pendulum).
        const dx = pivotX - body.position.x;
        const dy = pivotY - body.position.y;
        const dist = Math.hypot(dx, dy);
        if (dist < 1e-6) return [];
        const ux = dx / dist;
        const uy = dy / dist;
        // uy > 0 means the rope points upward from bob toward pivot (canvas Y-down convention).
        const gravComp = engine.gravity.y * body.mass * uy;
        const vSq = body.velocity.x * body.velocity.x + body.velocity.y * body.velocity.y;
        const centripetal = (body.mass * vSq) / length;
        const t = gravComp + centripetal;
        return [
          {
            origin: { ...body.position },
            components: { x: ux * t, y: uy * t },
            labelKey: 'force.tension',
            color: '#e91e63',
          },
        ];
      },
      dispose() {
        Matter.World.remove(world, [...bodies, ...constraints]);
      },
    };
  },
};
